import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "src/layout/admin";
import { useNavigate } from "react-router-dom";
import { getMethodApiCall } from "src/services/api";
import { useAuth } from "src/context/AuthContext";
import useWindowSize from "src/hooks/useWindowSize";
import "./Dashboard.css";
import DonutChart from "src/components/custom/DonutChart";
import HorizontalBarChart from "src/components/custom/HorizontalBarChart";
import HorizontalBarChartSlider from "src/components/custom/HorizontalBarChartSlider";
import DashboardFilters from "src/components/custom/DashboardFilters";
import { safeData } from "src/utils/helpers";

const COLORS = ["#2ec4b6", "#cbf3f0", "#a0e9e0", "#b2f7ef", "#ffb6b9"];

const getQueryPrefix = (filters) => {
  const params = new URLSearchParams();
  if (filters.from) params.append("from", filters.from);
  if (filters.to) params.append("to", filters.to);
  if (filters.district) params.append("district", filters.district);
  if (filters.iti) params.append("iti", filters.iti);
  const query = params.toString();
  return query ? `?${query}` : "";
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const { token } = useAuth();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    district: "",
    iti: "",
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [dateError, setDateError] = useState("");

  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [dashboardData, setDashboardData] = useState({
    gender: [],
    verification: [],
    attendanceComparison: [],
    attendanceByMonth: [],
    district: [],
    iti: [],
  });

  const [summary, setSummary] = useState({
    totalStudents: "-",
    presentStudents: "-",
    trainers: "-",
  });

  const [districts, setDistricts] = useState([]);
  const [itis, setItis] = useState([]);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const validateDates = (from, to) => {
    if (from && to && new Date(from) > new Date(to)) {
      setDateError("End date must be after start date");
      return false;
    }
    setDateError("");
    return true;
  };

  const fetchDashboard = useCallback(async (appliedFilters) => {
    const query = getQueryPrefix(appliedFilters);
    setLoading(true);
    setSummaryLoading(true);

    try {
      const [
        genderRes,
        verificationRes,
        attendanceCompRes,
        attendanceMonthRes,
        districtRes,
        itiRes,
        summaryRes,
      ] = await Promise.all([
        getMethodApiCall(`/v1/api/admin/dashboard/gender-percentage${query}`),
        getMethodApiCall(`/v1/api/admin/dashboard/verification-status${query}`),
        getMethodApiCall(
          `/v1/api/admin/dashboard/attendance-comparison${query}`,
        ),
        getMethodApiCall(`/v1/api/admin/dashboard/attendance-by-month${query}`),
        getMethodApiCall(
          `/v1/api/admin/dashboard/students-per-district${query}`,
        ),
        getMethodApiCall(`/v1/api/admin/dashboard/students-per-iti${query}`),
        getMethodApiCall(`/v1/api/admin/dashboard/summary-card${query}`),
      ]);


      const attendanceDataRaw = attendanceCompRes?.data?.data;

      const formattedAttendance = attendanceDataRaw
        ? [
            { name: "Present", value: attendanceDataRaw.present || 0 },
            { name: "Absent", value: attendanceDataRaw.absent || 0 },
          ]
        : [];

      setDashboardData({
        gender: safeData(genderRes) || [],
        verification: safeData(verificationRes) || [],
        attendanceComparison: formattedAttendance,
        attendanceByMonth: safeData(attendanceMonthRes) || [],
        district: safeData(districtRes) || [],
        iti: safeData(itiRes) || [],
      });

      setSummary({
        totalStudents: summaryRes?.data?.totalStudents || 0,
        presentStudents: summaryRes?.data?.presentStudents || 0,
        trainers: summaryRes?.data?.instructorCount || 0,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setSummaryLoading(false);
    }
  }, []);

  const fetchDistricts = async () => {
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/get_district_picklist`,
      );
      if (res?.data?.success) {
        setDistricts(res.data.data || []);
        setItis(res.data.itis || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboard(filters);
      fetchDistricts();
    }
  }, [token]);

  const handleApply = useCallback(() => {
    if (!dateError) {
      setFilters(tempFilters);
      fetchDashboard(tempFilters);
      setShowFilterDrawer(false);
    }
  }, [tempFilters, dateError]);

  const handleClear = useCallback(() => {
    const cleared = { from: "", to: "", district: "", iti: "" };
    setFilters(cleared);
    setTempFilters(cleared);
    fetchDashboard(cleared);
    setShowFilterDrawer(false);
  }, []);

    const handleTempFilterChange = async (updatedFilters) => {
    // If district changed → call API
    if (updatedFilters.district !== tempFilters.district) {
      if (updatedFilters.district) {
        const itiList = await fetchItisList(updatedFilters.district);
        setItis(itiList); // update ITIs from backend
      } else {
        setItis([]); // reset if no district
      }

      // reset iti when district changes
      updatedFilters.iti = "";
    }

    setTempFilters(updatedFilters);
  };

    const fetchItisList = async (district) => {
      if (!district) return [];
      try {
        const res = await getMethodApiCall(
          `/v1/api/trainee/get_iti_picklist/${district}`
        );
        return res?.data?.data || [];
      } catch {
        return [];
      }
    };

  return (
    <AdminLayout>
      <div className="flex justify-between mb-4">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <button
          onClick={() => setShowFilterDrawer(true)}
          className="bg-[#0E766c] text-white px-3 py-2 rounded"
        >
          FILTERS
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
        <div className="md:col-span-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {summaryLoading ? (
              Array(3)
                .fill(0)
                .map((_, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl shadow-md p-4 h-[6.25rem] flex items-center justify-center"
                  >
                    Loading...
                  </div>
                ))
            ) : (
              <>
                {/* Total Students */}
                <div
                  className="bg-white rounded-xl shadow-md p-3 h-[6.25rem] flex flex-col items-center justify-center cursor-pointer hover:shadow-md"
                  onClick={() => navigate("/admin/attendance")}
                >
                  <div className="text-2xl font-bold text-primary">
                    {summary.totalStudents}
                  </div>
                  <div className="text-sm font-medium">Total Students</div>
                </div>

                {/* Attendance */}
                <div
                  className="bg-white rounded-xl shadow-md p-3 h-[6.25rem] flex flex-col items-center justify-center cursor-pointer hover:shadow-md"
                  onClick={() => navigate("/admin/attendance")}
                >
                  <div className="text-2xl font-bold text-primary">
                    {summary.presentStudents}
                  </div>
                  <div className="text-sm font-medium">Students Attendance</div>
                </div>

                {/* Trainers */}
                <div
                  className="bg-white rounded-xl shadow-md p-3 h-[6.25rem] flex flex-col items-center justify-center cursor-pointer hover:shadow-md"
                  onClick={() => navigate("/admin/instructors")}
                >
                  <div className="text-2xl font-bold text-primary">
                    {summary.trainers}
                  </div>
                  <div className="text-sm font-medium">Trainers</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <DashboardFilters
        open={showFilterDrawer}
        tempFilters={tempFilters}
        districts={districts}
        itis={itis}
        dateError={dateError}
        onClose={() => setShowFilterDrawer(false)}
        onChange={handleTempFilterChange}
        onApply={handleApply}
        onClear={handleClear}
        onDateChange={(field, value) => {
          const updated = { ...tempFilters, [field]: value };
          setTempFilters(updated);
          validateDates(updated.from, updated.to);
        }}
      />

      <div className="grid grid-cols-3 gap-4">
        <DonutChart
          data={dashboardData.gender}
          colors={COLORS}
          title="Gender"
          loadingCharts={loading}
        />
        <DonutChart
          data={dashboardData.verification}
          colors={COLORS}
          title="Verification"
          loadingCharts={loading}
        />
        <DonutChart
          data={dashboardData.attendanceComparison}
          colors={COLORS}
          title="Attendance"
          loadingCharts={loading}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <HorizontalBarChart
          title="Attendance"
          data={dashboardData.attendanceByMonth}
          loading={loading}
          color="#2ec4b6"
        />
        <HorizontalBarChartSlider
          districtData={dashboardData.district}
          itiData={dashboardData.iti}
          loading={loading}
          title="Students per District & ITI"
        />
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
